import React, { lazy, Suspense } from 'react';
import { LucideProps } from 'lucide-react';
import dynamicIconImports from 'lucide-react/dynamicIconImports';

interface DynamicLucideIconProps extends Omit<LucideProps, 'ref'> {
  name: string;
  fallback?: React.ReactNode;
}

type IconKey = keyof typeof dynamicIconImports;

// Module-level cache: each icon is only lazy-loaded once
const iconCache = new Map<IconKey, React.LazyExoticComponent<React.ComponentType<LucideProps>>>();

function getIconComponent(name: IconKey) {
  if (!iconCache.has(name)) {
    iconCache.set(name, lazy(dynamicIconImports[name]));
  }
  return iconCache.get(name)!;
}

export function DynamicLucideIcon({ name, fallback, ...props }: DynamicLucideIconProps) {
  const kebabCaseName = name
    .replace(/([A-Z])/g, (_, letter, index) =>
      index > 0 ? `-${letter.toLowerCase()}` : letter.toLowerCase()
    )
    .toLowerCase();

  if (!(kebabCaseName in dynamicIconImports)) {
    console.warn(`Icon "${name}" (${kebabCaseName}) not found in lucide-react`);
    return fallback || <div className="w-8 h-8 bg-gray-200 rounded" />;
  }

   
  const LucideIcon = getIconComponent(kebabCaseName as IconKey);

  const defaultFallback = (
    <div
      className="bg-gray-200 rounded animate-pulse"
      style={{ width: props.size || 24, height: props.size || 24 }}
    />
  );

  return (
    <Suspense fallback={fallback || defaultFallback}>
      {React.createElement(LucideIcon, props)}
    </Suspense>
  );
}
